import { useState, useEffect } from "react";
import { useForm } from "react-hook-form";
import { useMutation, useQuery } from "@tanstack/react-query";
import { queryClient } from "@/lib/queryClient";
import {
  createEntityMapping,
  updateEntityMapping,
  fetchDataverseEntities,
  fetchEntityFields,
} from "@/lib/api";
import {
  EntityMappingDTO,
  FieldPairDTO,
  DataverseEntityInfo,
  DataverseFieldInfo,
} from "@shared/api-types";
import { FieldCombobox } from "./field-combobox";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { useToast } from "@/hooks/use-toast";
import { Loader2, Trash2, Wand2, RefreshCw, Check, ChevronDown } from "lucide-react";
import { 
  Popover,
  PopoverContent,
  PopoverTrigger
} from "@/components/ui/popover";
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
} from "@/components/ui/command";
import { cn } from "@/lib/utils";
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/components/ui/form";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { EntityCombobox } from "./entity-combobox";

interface EntityMappingFormProps {
  mapping?: EntityMappingDTO;
  onSubmit: () => void;
  onCancel: () => void;
}

interface FormValues {
  parentEntity: string;
  childEntity: string;
  childLookupField: string;
  parentLookupField: string;
  fieldMappings: FieldPairDTO[];
}

export default function EntityMappingFormNew({
  mapping,
  onSubmit,
  onCancel,
}: EntityMappingFormProps) {
  const { toast } = useToast();
  const [formError, setFormError] = useState<string | null>(null);
  const [openParentFieldPopover, setOpenParentFieldPopover] = useState<number | null>(null);
  const [openChildFieldPopover, setOpenChildFieldPopover] = useState<number | null>(null);
  const [excludeBuiltin, setExcludeBuiltin] = useState<boolean>(true);
  const [fieldMappingsState, setFieldMappingsState] = useState<FieldPairDTO[]>(
    mapping ? [...mapping.fieldMappings] : [{ parentField: "", childField: "" }]
  );

  // Define isEditMode for cleaner conditional rendering
  const isEditMode = !!mapping?.rowKey;
  const isDuplicateMode = !!mapping && !mapping.rowKey;

  // Initialize form with default values or passed in mapping
  const form = useForm<FormValues>({
    defaultValues: mapping
      ? {
          parentEntity: mapping.parentEntity,
          childEntity: mapping.childEntity,
          childLookupField: mapping.childLookupField,
          parentLookupField: mapping.parentLookupField,
          fieldMappings: [...mapping.fieldMappings],
        }
      : {
          parentEntity: "",
          childEntity: "",
          childLookupField: "",
          parentLookupField: "",
          fieldMappings: [{ parentField: "", childField: "" }],
        },
    mode: "onChange", // Enable validation on change
  });

  // Create queries for entities, fields, and lookupFields
  const { data: entities, isLoading: isLoadingEntities } = useQuery({
    queryKey: ["/api/entity-mappings/entities"],
    queryFn: () => fetchDataverseEntities(),
    enabled: true, // Fetch entities for both new and existing mappings
    staleTime: 300000, // Cache data for 5 minutes
  });

  const [selectedParentEntity, setSelectedParentEntity] = useState<string>(
    isEditMode || isDuplicateMode ? mapping.parentEntity : "",
  );

  const [selectedChildEntity, setSelectedChildEntity] = useState<string>(
    isEditMode || isDuplicateMode ? mapping.childEntity : "",
  );

  // Update the selected entities when the form values change
  useEffect(() => {
    const parentEntity = form.getValues().parentEntity;
    const childEntity = form.getValues().childEntity;

    if (parentEntity !== selectedParentEntity) {
      setSelectedParentEntity(parentEntity);
    }

    if (childEntity !== selectedChildEntity) {
      setSelectedChildEntity(childEntity);
    }
  }, [form.watch("parentEntity"), form.watch("childEntity")]);
  
  // Refetch fields when excludeBuiltin toggle changes
  useEffect(() => {
    // Only refetch if we have selected entities
    if (selectedParentEntity) {
      // Invalidate the query cache for fields to trigger a refetch
      queryClient.invalidateQueries({ 
        queryKey: ["/api/entity-mappings/fields", selectedParentEntity]
      });
      queryClient.invalidateQueries({ 
        queryKey: ["/api/entity-mappings/lookupFields", selectedParentEntity]
      });
    }
    
    if (selectedChildEntity) {
      queryClient.invalidateQueries({ 
        queryKey: ["/api/entity-mappings/fields", selectedChildEntity]
      });
      queryClient.invalidateQueries({ 
        queryKey: ["/api/entity-mappings/lookupFields", selectedChildEntity]
      });
    }
  }, [excludeBuiltin, selectedParentEntity, selectedChildEntity]);

  // Get parent entity from form or mapping
  const parentEntityValue = isEditMode || isDuplicateMode
    ? mapping.parentEntity
    : selectedParentEntity;
  const childEntityValue = isEditMode || isDuplicateMode ? mapping.childEntity : selectedChildEntity;

  // Fetch parent entity fields
  const { data: parentFields, isLoading: isLoadingParentFields } = useQuery({
    queryKey: ["/api/entity-mappings/fields", parentEntityValue, excludeBuiltin],
    queryFn: () => fetchEntityFields(parentEntityValue, excludeBuiltin),
    enabled: !!parentEntityValue, // Fetch for both new and existing mappings
    staleTime: 6000, // Cache data for 1 minute
  });

  // Fetch child entity fields
  const { data: childFields, isLoading: isLoadingChildFields } = useQuery({
    queryKey: ["/api/entity-mappings/fields", childEntityValue, excludeBuiltin],
    queryFn: () => fetchEntityFields(childEntityValue, excludeBuiltin),
    enabled: !!childEntityValue, // Fetch for both new and existing mappings
    staleTime: 6000, // Cache data for 1 minute
  });

  // Fetch child entity lookup fields
  const {
    data: childLookupFields,
    isLoading: isLoadingChildLookupFields,
    error: childLookupError,
  } = useQuery({
    queryKey: ["/api/entity-mappings/lookupFields", childEntityValue, excludeBuiltin],
    queryFn: () => fetchEntityFields(childEntityValue, excludeBuiltin),
    enabled: !!childEntityValue, // Fetch for both new and existing mappings
    staleTime: 6000, // Cache data for 1 minute
    retry: false, // Don't retry on failure
  });

  // Fetch parent entity lookup fields
  const {
    data: parentLookupFields,
    isLoading: isLoadingParentLookupFields,
    error: parentLookupError,
  } = useQuery({
    queryKey: ["/api/entity-mappings/lookupFields", parentEntityValue, excludeBuiltin],
    queryFn: () => fetchEntityFields(parentEntityValue, excludeBuiltin),
    enabled: !!parentEntityValue, // Fetch for both new and existing mappings
    staleTime: 6000, // Cache data for 1 minute
    retry: false, // Don't retry on failure
  });

  // Create or update entity mapping
  const mutation = useMutation({
    mutationFn: (data: FormValues) => {
      if (isEditMode) {
        // Update existing mapping
        return updateEntityMapping({
          parentEntity: mapping.parentEntity,
          childEntity: mapping.childEntity,
          parentLookupField: data.parentLookupField,
          childLookupField: data.childLookupField,
          fieldMappings: data.fieldMappings,
        });
      } else {
        // Create new mapping (for new or duplicate)
        return createEntityMapping(data);
      }
    },
    onSuccess: () => {
      toast({
        title: `Entity mapping ${isEditMode ? "updated" : "created"} successfully`,
        description: "The entity mapping has been saved.",
      });
      onSubmit();
    },
    onError: (error: Error) => {
      console.error("Error saving entity mapping:", error);
      setFormError(
        error.message || "An error occurred while saving the entity mapping.",
      );
      toast({
        title: "Error saving entity mapping",
        description:
          error.message || "An error occurred while saving the entity mapping.",
        variant: "destructive",
      });
    },
  });

  // Add a new field mapping to the form
  const addFieldMapping = () => {
    const currentFieldMappings = form.getValues().fieldMappings || [];
    const newFields = [...currentFieldMappings, { parentField: "", childField: "" }];
    form.setValue("fieldMappings", newFields);
    setFieldMappingsState(newFields);
  };

  // Remove a field mapping from the form
  const removeFieldMapping = (index: number) => {
    const currentFieldMappings = form.getValues().fieldMappings || [];

    if (currentFieldMappings.length <= 1) {
      toast({
        title: "Cannot remove field mapping",
        description: "At least one field mapping is required.",
        variant: "destructive",
      });
      return;
    }

    const newFieldMappings = currentFieldMappings.filter((_, i) => i !== index);
    form.setValue("fieldMappings", newFieldMappings);
    setFieldMappingsState(newFieldMappings);
  };

  // Auto-match fields based on name similarity
 

  // Helper to safely extract the logical name with proper preservation
  const getFieldLogicalName = (field: DataverseFieldInfo): string => {
    // Make sure we're getting the exact logical name from the API response
    // LogicalName is the case-sensitive name with prefixes like "pl_"
    const logicalName = field.LogicalName || field.logicalName || "";
    console.log("Extracted logical name:", logicalName);
    return logicalName;
  };
  
  // Helper to get display name for UI presentation
  const getFieldDisplayName = (field: DataverseFieldInfo): string => {
    return field.DisplayName?.UserLocalizedLabel?.Label || 
           field.DisplayName?.LocalizedLabels?.[0]?.Label || 
           field.SchemaName || 
           field.displayName || 
           field.LogicalName ||
           field.logicalName || 
           "";
  };

  // Handle form submission
  const handleSubmit = async (data: FormValues) => {
    console.log("Submitting entity mapping form:", data);

    // Validate required fields
    if (!data.parentEntity) {
      setFormError("Parent entity is required");
      return;
    }

    if (!data.childEntity) {
      setFormError("Child entity is required");
      return;
    }

    if (!data.childLookupField) {
      setFormError("Child lookup field is required");
      return;
    }

    if (!data.parentLookupField) {
      setFormError("Parent lookup field is required");
      return;
    }

    // Validate that all field mappings have both parent and child fields
    const invalidFieldMappings = data.fieldMappings.filter(
      (fieldPair) => !fieldPair.parentField || !fieldPair.childField,
    );

    if (invalidFieldMappings.length > 0) {
      setFormError(
        "All field mappings must have both parent and child fields selected",
      );
      return;
    }

    // Use the form data directly - values already contain the correct logical names
    // The only transformation we need is to make parentLookupField undefined if it's "_none"
    const submissionData = {
      ...data
    };

    console.log("Data for API submission:", submissionData);
    
    setFormError(null);
    mutation.mutate(submissionData);
  };

  const isSubmitting = mutation.isPending;

  // Render just the form content
  return (
    <Form {...form}>
      <form
        onSubmit={form.handleSubmit(handleSubmit)}
        className="space-y-4"
      >
        <FormField
          control={form.control}
          name="parentEntity"
          render={({ field }) => (
            <FormItem>
              <FormLabel>Parent Entity</FormLabel>
              <FormControl>
                <EntityCombobox
                  {...field}
                  entities={entities || []}
                  isLoading={isLoadingEntities}
                />
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />

        <FormField
          control={form.control}
          name="childEntity"
          render={({ field }) => (
            <FormItem>
              <FormLabel>Child Entity</FormLabel>
              <FormControl>
                <EntityCombobox
                  {...field}
                  entities={entities || []}
                  isLoading={isLoadingEntities}
                />
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />

        <div className="flex space-x-4">
          <FormField
            control={form.control}
            name="parentLookupField"
            render={({ field }) => (
              <FormItem className="flex-1">
                <FormLabel>Parent Lookup</FormLabel>
                <FormControl>
                  <FieldCombobox
                    {...field}
                    fields={parentLookupFields || []}
                    isLoading={isLoadingParentLookupFields}
                  />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />

          <FormField
            control={form.control}
            name="childLookupField"
            render={({ field }) => (
              <FormItem className="flex-1">
                <FormLabel>Child Lookup</FormLabel>
                <FormControl>
                  <FieldCombobox
                    {...field}
                    fields={childLookupFields || []}
                    isLoading={isLoadingChildLookupFields}
                  />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />
        </div>
<div className="flex items-center space-x-2">
<div className="flex-1">
  <Label className="text-sm font-medium">
    Parent Fields
  </Label>
</div>
<div className="flex-1">
  <Label className="text-sm font-medium">
    Child Fields
  </Label>
</div>
</div>
        {fieldMappingsState.map((fieldMapping, idx) => (
          <div key={idx} className="flex items-center space-x-2">
            <FormField
              control={form.control}
              name={`fieldMappings.${idx}.parentField`}
              render={({ field }) => (
                <FormItem className="flex-1">
                  <FormControl>
                    <FieldCombobox
                      {...field}
                      fields={parentFields || []}
                      isLoading={isLoadingParentFields}
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            <FormField
              control={form.control}
              name={`fieldMappings.${idx}.childField`}
              render={({ field }) => (
                <FormItem className="flex-1">
                  <FormControl>
                    <FieldCombobox
                      {...field}
                      fields={childFields || []}
                      isLoading={isLoadingChildFields}
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            <Button
              type="button"
              variant="destructive"
              size="icon"
              onClick={() => removeFieldMapping(idx)}
            >
              <Trash2 size={16} />
            </Button>
          </div>
        ))}

        <Button
          type="button"
          variant="secondary"
          onClick={addFieldMapping}
        >
          Add Field
        </Button>
        
        {formError && (
          <div className="p-3 text-sm font-medium text-white bg-red-500 rounded">
            {formError}
          </div>
        )}

        <div className="flex justify-end space-x-2 pt-4">
          <Button
            type="button"
            variant="outline"
            onClick={onCancel}
          >
            Cancel
          </Button>
          <Button type="submit" disabled={isSubmitting}>
            {isSubmitting ? <Loader2 className="animate-spin mr-2" size={16} /> : null}
            {isEditMode
              ? "Update"
              : isDuplicateMode
                ? "Create Duplicate"
                : "Create"}
          </Button>
        </div>
      </form>
    </Form>
  );
}